import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  IConnection,
  IConnectionWithScopedCouplings,
  ICoupling,
  IDataConnectInboundAuth,
  IDataConnectInboundEquipmentList,
  IDataConnectInboundContainerPut,
  IDataConnectInboundCouplingList,
  TRACKER_SCOPE,
  DATACONNECT_INBOUND_SCOPE,
} from '@cde-fe-app/app/shared/models/connections.model';
import { environment } from '@cde-fe-app/environments/environment';
import { RequestParam, ServiceCall } from './service-call.service';

export const CONNECTIONS_URL = 'v1/connections';

@Injectable({
  providedIn: 'root',
})
export class ConnectionsService {
  private serviceCall = inject(ServiceCall);
  private connectionsURL = `${environment.apiBaseUrl}/${CONNECTIONS_URL}`;

  getConnections(languageCode: string): Observable<IConnection[]> {
    let params: RequestParam = new HttpParams();
    params = params.set('languageCode', languageCode);
    return this.serviceCall.get(`${this.connectionsURL}`, params).pipe(map(response => response.data as IConnection[]));
  }

  getConnection(id: string, languageCode: string): Observable<IConnectionWithScopedCouplings> {
    let params: RequestParam = new HttpParams();
    params = params.set('languageCode', languageCode);
    return this.serviceCall.get(`${this.connectionsURL}/${id}`, params).pipe(
      map(response => ({
        ...(response.data as IConnection),
        couplingForScope: this.getCouplingsForScopes(response.data as IConnection),
      }))
    );
  }

  getPartnerLogo(connectionId: string): Observable<{ id: string; logoUrl: string }> {
    return this.serviceCall.getImage(`${this.connectionsURL}/${connectionId}/partner/logo`).pipe(
      catchError(error => {
        if (error.data.status === 404) {
          return of({ data: null });
        }
        return of(undefined);
      }),
      map(
        response =>
          ({ id: connectionId, logoUrl: response?.data ? URL.createObjectURL(response.data as Blob) : null }) as {
            id: string;
            logoUrl: string;
          }
      )
    );
  }

  revokeCoupling(connection: IConnection, coupling: ICoupling) {
    return this.serviceCall.patch(`${this.connectionsURL}/${connection.id}/couplings/${coupling.id}`, {
      state: 'revoked',
    });
  }

  getCouplingsForScopes(connection: IConnection | undefined | null): Map<string, ICoupling[] | null | undefined> {
    const couplingsForScope = new Map<string, ICoupling[] | null | undefined>();
    if (!connection) {
      return couplingsForScope;
    }

    connection.partner.scopes.forEach(scope => {
      const targetScope = scope === TRACKER_SCOPE ? DATACONNECT_INBOUND_SCOPE : scope;
      couplingsForScope.set(
        scope,
        connection.couplings.filter(coupling => coupling.scope?.split(' ').includes(targetScope))
      );
    });

    return couplingsForScope;
  }

  getDataConnectCouplings(connectionId: string): Observable<IDataConnectInboundCouplingList> {
    return this.serviceCall
      .get(`${this.connectionsURL}/${connectionId}/scopes/dataconnect-inbound/couplings`)
      .pipe(map(response => response.data as IDataConnectInboundCouplingList));
  }

  getDataConnectInboundAuth(
    connectionId: string | undefined,
    state?: string,
    code?: string,
    error?: string,
    errorDescription?: string
  ): Observable<IDataConnectInboundAuth> {
    let params = new HttpParams();

    if (state) {
      params = params.append('state', state);
    }
    if (code) {
      params = params.append('code', code);
    }

    if (error) {
      params = params.append('error', error);
    }

    if (errorDescription) {
      params = params.append('errorDescription', errorDescription);
    }

    return this.serviceCall
      .get(`${this.connectionsURL}/${connectionId}/scopes/dataconnect-inbound/couplings/auth`, params)
      .pipe(map(response => response.data as IDataConnectInboundAuth));
  }

  fetchEquipment(
    connectionId: string,
    containerId: string,
    couplingId: string
  ): Observable<IDataConnectInboundEquipmentList> {
    const params = new HttpParams({
      fromObject: { containerId: containerId ? containerId : '' },
    });

    const equipmentUrl = `${this.connectionsURL}/${connectionId}/scopes/dataconnect-inbound/couplings/${couplingId}/equipment`;

    return this.serviceCall
      .get(equipmentUrl, params)
      .pipe(map(response => response.data as IDataConnectInboundEquipmentList));
  }

  putDataConnectContainer(connectionId: string, couplingId: string, payload: IDataConnectInboundContainerPut) {
    return this.serviceCall.put(
      `${this.connectionsURL}/${connectionId}/scopes/dataconnect-inbound/couplings/${couplingId}/container`,
      payload
    );
  }

  deleteDataConnectCoupling(connectionId: string, couplingId: string) {
    return this.serviceCall.delete(
      `${this.connectionsURL}/${connectionId}/scopes/dataconnect-inbound/couplings/${couplingId}`
    );
  }
}
